import { useEffect, useState, type ReactNode } from 'react';
import { validateTarget } from '@lw/shared/mission-fsm';
import type { ClientSnapshot } from '@lw/client-core';
import { useRuntime } from '../runtime/useRuntime.js';
import { actedBy, fmtTime } from '../app/helpers.js';
import { ActedBy, EstimateChip, PeerChip, ReadyAge, SolutionSummary, SyncChip } from '../components/Chips.js';
import { CountdownPanel } from '../components/Countdown.js';
import { ReviewCard } from '../components/Common.js';

export interface ViewOut {
  main: ReactNode;
  actions?: ReactNode;
}

export function useSeat(): boolean {
  const { rt, state } = useRuntime();
  const role = rt.id.role;
  if (role !== 'UAV' && role !== 'FDC') return false;
  const seat = state.snapshot?.seats[role];
  return !seat || seat.deviceId === rt.id.deviceId;
}

export function UavView({ snapshot: s }: { snapshot: ClientSnapshot }): ViewOut {
  const { rt, state } = useRuntime();
  const ctrl = useSeat();
  const events = state.events;
  const [mode, setMode] = useState<'NAME' | 'MGRS'>((localStorage.getItem('lw.tgtMode') as 'NAME' | 'MGRS' | null) ?? 'NAME');
  const [tgt, setTgt] = useState(localStorage.getItem('lw.tgt') ?? '');
  const [audioTest, setAudioTest] = useState<'none' | 'played' | 'heard' | 'skipped'>('none');
  useEffect(() => {
    if (s.state !== 'WEAPON_READY') setAudioTest('none');
  }, [s.state]);

  switch (s.state) {
    case 'IDLE':
    case 'SAVED':
    case 'DISCARDED': {
      const v = validateTarget({ type: mode, value: tgt });
      const canonical = v.ok && v.value ? v.value.value : '';
      const precision = mode === 'MGRS' && v.ok ? ({ 2: '10 km', 4: '1 km', 6: '100 m', 8: '10 m', 10: '1 m' } as Record<number, string>)[canonical.replace(/^\d{1,2}[A-Z]{3}/, '').length] : '';
      const last = s.missionId && s.state !== 'IDLE' ? s : null;
      return {
        main: (
          <>
            {last && last.state === 'SAVED' ? (
              <div className="card green">
                <div className="eyebrow">Mission log saved</div>
                <h2>{last.missionId}</h2>
                <p>Every command and event from all devices, with local and server timestamps and measured delays.</p>
                <div className="row" style={{ marginTop: 10 }}>
                  <a className="btn" href={`/api/v1/missions/${last.missionId}/export?device=${rt.id.deviceId}&user=${rt.id.user}&role=${rt.id.role}`} download>Download log</a>
                </div>
              </div>
            ) : null}
            {last && last.state === 'DISCARDED' ? (
              <div className="card"><div className="eyebrow">Mission discarded</div><p>Nothing was kept for this call for fire.</p></div>
            ) : null}
            <div className="card">
              <div className="eyebrow">New call for fire</div>
              <h2>Target</h2>
              <div className="row" style={{ marginTop: 10 }}>
                <div className="seg">
                  <button className={mode === 'NAME' ? 'on' : ''} onClick={() => { setMode('NAME'); localStorage.setItem('lw.tgtMode', 'NAME'); }}>Target name</button>
                  <button className={mode === 'MGRS' ? 'on' : ''} onClick={() => { setMode('MGRS'); localStorage.setItem('lw.tgtMode', 'MGRS'); }}>MGRS</button>
                </div>
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor="tgt">{mode === 'NAME' ? 'Target name' : 'MGRS grid'}</label>
                <input id="tgt" className="mono" autoCapitalize="characters" value={tgt} placeholder={mode === 'NAME' ? 'TGT1' : '35VLF41238765'} onChange={(e) => { setTgt(e.target.value); localStorage.setItem('lw.tgt', e.target.value); }} data-testid="target-input" />
                <div className={`hint ${v.ok ? 'ok' : 'err'}`}>
                  {v.ok ? (mode === 'NAME' ? `Target name accepted — ${canonical}` : `Valid MGRS, ${precision} precision — sent as ${canonical}`) : tgt ? (mode === 'NAME' ? '1–16 letters, digits, space, _ or -' : 'Zone, band letter, 100 km square, then an even number of digits (spaces are removed)') : 'Enter the target'}
                </div>
              </div>
            </div>
            <MissionHistory />
          </>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn primary" disabled={!v.ok || !ctrl} data-testid="cff" onClick={() => rt.client.send('CREATE_CFF', { target: { type: mode, value: tgt } }, { basedOnSeq: 0 })}>Call for fire</button>
          </div></div>
        ),
      };
    }
    case 'CFF':
      return {
        main: (
          <div className="card amber">
            <div className="eyebrow">Call for fire sent</div>
            <h2 className="big">{s.target?.value}</h2>
            <p>Awaiting the FDC. They will confirm the mission and give a time estimate.</p>
            <ActedBy by={actedBy(events, 'CFF_CREATED')} />
          </div>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn outline-danger" disabled={!ctrl} onClick={() => rt.client.send('BACK')}>Withdraw call for fire</button>
          </div></div>
        ),
      };
    case 'SOLUTION':
      return { main: <div className="card"><div className="eyebrow">Fire mission confirmed</div><h2 className="big">FDC computing solution</h2><div className="row" style={{ marginTop: 10 }}><EstimateChip /></div><ActedBy by={actedBy(events, 'MISSION_STARTED')} /></div> };
    case 'PROGRAMMING':
      return { main: <div className="card"><div className="eyebrow">Programming shell</div><div className="codeecho">{s.solution?.laserCode}<small>Laser code</small></div><div className="row"><EstimateChip /></div><ActedBy by={actedBy(events, 'SOLUTION_ENTERED')} /></div> };
    case 'LOADING':
      return { main: <div className="card"><div className="eyebrow">Loading weapon</div><h2 className="big">Gun being loaded</h2><div className="row" style={{ marginTop: 10 }}><EstimateChip /></div><SolutionSummary /><ActedBy by={actedBy(events, 'PROGRAMMING_DONE')} /></div> };
    case 'WEAPON_READY': {
      const t = audioTest;
      const play = (): void => {
        void rt.audio.arm().then(() => {
          rt.cues.selfTest();
          setAudioTest('played');
        });
      };
      return {
        main: (
          <>
            <div className="card green">
              <div className="eyebrow">Weapon ready</div>
              <h2 className="big">Confirm you are ready</h2>
              <p>The gun is loaded. Check your audio, then press UAV READY when the aircraft, camera and designator are set.</p>
              <SolutionSummary />
              <ActedBy by={actedBy(events, 'LOADING_DONE')} />
            </div>
            <div className={`card ${t === 'heard' ? 'green' : t === 'skipped' ? 'amber' : ''}`} data-testid="audio-check">
              <div className="eyebrow">Audio check</div>
              {t === 'none' ? <><p>Play a test tone and confirm you heard it. Readiness stays locked until you do.</p><div className="row" style={{ marginTop: 10 }}><button className="btn" onClick={play} data-testid="test-tone">Play test tone</button></div></> : null}
              {t === 'played' ? <><p>Did you hear the tone?</p><div className="row" style={{ marginTop: 10 }}><button className="btn confirm" data-testid="heard" onClick={() => { rt.cues.recordSelfTest('HEARD'); setAudioTest('heard'); }}>Heard it</button><button className="btn ghost" onClick={play}>Play again</button><button className="btn ghost" onClick={() => { rt.cues.recordSelfTest('SKIPPED'); setAudioTest('skipped'); }}>Skip — no audio</button></div></> : null}
              {t === 'heard' ? <p className="green">Audio confirmed. Warning beeps and tones will be heard on this device.</p> : null}
              {t === 'skipped' ? <p className="amber">Audio skipped — visual cues only on this device. This is recorded in the log.</p> : null}
            </div>
          </>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn confirm huge" disabled={!(t === 'heard' || t === 'skipped') || !ctrl} data-testid="uav-ready" onClick={() => rt.client.send('UAV_READY', { audioSelfTest: t === 'heard' ? 'HEARD' : 'SKIPPED' }, { expectedState: 'WEAPON_READY' })}>UAV ready</button>
          </div></div>
        ),
      };
    }
    case 'UAV_READY':
      return {
        main: (
          <div className="card green">
            <div className="eyebrow">Ready</div>
            <h2 className="big">Stand by for shot</h2>
            <p>Ready for <ReadyAge />. You will hear a three-beep alarm when the gun fires.</p>
            <div className="row" style={{ marginTop: 10 }}><PeerChip /><SyncChip /></div>
            <SolutionSummary />
            <ActedBy by={actedBy(events, 'UAV_READY_SET')} />
          </div>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn outline-amber" disabled={!ctrl} onClick={() => rt.client.send('UAV_NOT_READY', {}, { expectedState: 'UAV_READY' })}>Not ready</button>
          </div></div>
        ),
      };
    case 'FIRE_REVIEW':
      return { main: <ReviewCard snapshot={s} forFdc={false} /> };
    case 'IN_FLIGHT': {
      const tl = s.timeline;
      const now = rt.client.chrono.serverNow();
      const armWindow = !!tl && now !== null && now >= tl.tLaserOn - 5000;
      return {
        main: <CountdownPanel snapshot={s} />,
        actions: (
          <div className="actionbar"><div className="inner">
            {s.uav.laserOnConfirmedAt ? (
              <button className="btn confirm huge" disabled>Laser on ✓</button>
            ) : (
              <button className={`btn ${armWindow ? 'confirm' : ''} huge`} disabled={!armWindow || !ctrl} data-testid="laser-on" onClick={() => rt.client.send('LASER_ON_CONFIRM', {}, { expectedState: 'IN_FLIGHT' })}>Laser on</button>
            )}
          </div></div>
        ),
      };
    }
    case 'AWAITING_DISPOSITION':
      return {
        main: (
          <>
            <div className="card">
              <div className="eyebrow">Mission complete</div>
              <h2 className="big">{s.target?.value} · impact {fmtTime(s.timeline?.tImpact, true)}</h2>
              <p>Save keeps the full event log for download. Discard removes it.</p>
              <SolutionSummary />
            </div>
            {s.uav.laserOffAt ? null : (
              <div className="card amber">
                <div className="eyebrow">Laser still on?</div>
                <p>Confirm the designator is off so everyone hears the LASER OFF callout.</p>
                <div className="row" style={{ marginTop: 10 }}><button className="btn confirm" data-testid="laser-off" onClick={() => rt.client.send('LASER_OFF', {}, { expectedState: 'AWAITING_DISPOSITION' })}>Laser off</button></div>
              </div>
            )}
          </>
        ),
        actions: (
          <div className="actionbar"><div className="inner">
            <button className="btn outline-danger narrow" disabled={!ctrl} data-testid="discard" onClick={() => rt.client.send('DISCARD', {}, { expectedState: 'AWAITING_DISPOSITION' })}>Discard</button>
            <button className="btn confirm" disabled={!ctrl} data-testid="save" onClick={() => rt.client.send('SAVE', {}, { expectedState: 'AWAITING_DISPOSITION' })}>Save</button>
          </div></div>
        ),
      };
    default:
      return { main: null };
  }
}

export function MissionHistory(): ReactNode {
  const { rt } = useRuntime();
  const [rows, setRows] = useState<{ missionId: string; target: string; createdAt: number; disposition: string; state: string }[]>([]);
  useEffect(() => {
    let alive = true;
    fetch('/api/v1/missions', { headers: { 'x-lw-device': rt.id.deviceId, 'x-lw-user': rt.id.user, 'x-lw-role': rt.id.role } })
      .then((r) => (r.ok ? r.json() : []))
      .then((j: typeof rows) => alive && setRows(j.filter((m) => m.disposition === 'SAVED' || m.disposition === 'DISCARDED').slice(0, 6)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [rt]);
  if (!rows.length) return null;
  return (
    <div className="card">
      <div className="eyebrow">Previous missions</div>
      <div className="kv">
        {rows.map((m) => (
          <Fragment2 key={m.missionId}>
            <dt>{fmtTime(m.createdAt)}</dt>
            <dd>
              {m.target} · {m.disposition.toLowerCase()}
              {m.disposition === 'SAVED' && rt.id.role !== 'OBSERVER' ? <> · <a href={`/api/v1/missions/${m.missionId}/export?device=${rt.id.deviceId}&user=${rt.id.user}&role=${rt.id.role}`}>log</a></> : null}
            </dd>
          </Fragment2>
        ))}
      </div>
    </div>
  );
}

function Fragment2({ children }: { children: ReactNode }): ReactNode {
  return <>{children}</>;
}
